/**
 * @fileoverview How the signed-in user is shown in the UI: full name, initials
 * and role label. The backend sends `firstName`/`lastName` (some older payloads
 * a single `name`), so this reads whichever is there and never shows a
 * placeholder name for a real user.
 */

/** The fields of the auth `User` payload that naming reads. */
export interface DisplayableUser {
  firstName?: unknown;
  lastName?: unknown;
  name?: unknown;
  email?: unknown;
  role?: unknown;
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

/** "First Last", else `name`, else the email's local part, else "User". */
export function userDisplayName(user: DisplayableUser | null | undefined): string {
  if (!user) return 'User';
  const fullName = [text(user.firstName), text(user.lastName)].filter(Boolean).join(' ');
  return fullName || text(user.name) || text(user.email).split('@')[0] || 'User';
}

/** Up to two initials from the display name, e.g. "AS" for "Ana Silva". */
export function userInitials(user: DisplayableUser | null | undefined): string {
  const words = userDisplayName(user).split(/\s+/).filter(Boolean);
  const initials = words.length > 1 ? words[0][0] + words[words.length - 1][0] : words[0]?.[0];
  return (initials ?? 'U').toUpperCase();
}

/**
 * The role name for a badge, or null when the user has none. Not
 * `role_description`: that is a sentence about the role's access, not a label.
 */
export function userRoleLabel(user: DisplayableUser | null | undefined): string | null {
  return text(user?.role) || null;
}
